import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useSelector } from "react-redux";
import { getShorts, getSingleShorts } from './service';
// import Videos from "./Videos";
import Videos from "./Player";
// import Videos from "./ReactPlayer";
import { useNavigate, useLocation ,useParams } from 'react-router-dom';

// Debounce function to limit the scroll event firing
const debounce = (func, wait) => {
  let timeout;
  return function (...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const Shorts = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [allShorts, setAllShorts] = useState([]);
  const [currentBatch, setCurrentBatch] = useState([]);
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMoreShorts, setHasMoreShorts] = useState(true);
  const shortsContainerRef = useRef(null);
  const lastScrollTop = useRef(0); // Store the previous scroll position
  const projectInfo = useSelector((state) => state?.projectInfo?.value);
  const sessionId = useSelector((state) => state?.sessionId?.value);
  const accessToken = useSelector((state) => state?.accessToken?.value);
  const isRTL = useSelector((state) => state?.rtl?.isRTL);
  const [visibleVideoId, setVisibleVideoId] = useState(null);
  const [visibleVideo, setVisibleVideo] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [previousPath, setPreviousPath] = useState(null);
  const [previousVisibleVideoId, setPreviousVisibleVideoId] = useState(null);
  const { vanityUrl } = useParams(); 

  const appInfo = useMemo(() => ({ projectDetails: projectInfo, accessToken, sessionId }), [projectInfo, accessToken, sessionId]);

  const fetchMoreShorts = useCallback(async () => {
    if (isFetching || !hasMoreShorts) return;

    setIsFetching(true);
    try {
      const response = await getShorts(appInfo);
      if (response?.data?.data && response.data.data.length > 0) {
        const raw = response.data.data;
        const newBatch = raw.map((item, idx) => ({ ...item, _instanceId: `${item.video_id}-${Date.now()}-${Math.random().toString(36).slice(2,8)}` }));
        setCurrentBatch(newBatch);
        setCurrentBatchIndex(0);
        setAllShorts(prev => [...prev, ...newBatch]);
      } else {
        setHasMoreShorts(false);
      }
    } catch (error) {
      console.error('Error fetching shorts:', error);
    }
    setIsFetching(false);
  }, [isFetching, hasMoreShorts, appInfo, allShorts]);

  const checkAndFetchMore = useCallback(() => {
    if (currentBatchIndex >= currentBatch.length - 2 && hasMoreShorts && !isFetching) {
      fetchMoreShorts();
    }
  }, [currentBatchIndex, currentBatch.length, hasMoreShorts, isFetching, fetchMoreShorts]);

  const determineVisibleVideo = useCallback(async () => {
    if (!shortsContainerRef.current || allShorts.length === 0) return;
    
    const containerRect = shortsContainerRef.current.getBoundingClientRect();
    const visibleVideos = allShorts
      .filter((short) => {
        const instanceId = short._instanceId || short.video_id;
        const videoElement = document.querySelector(`[data-instance-id="${instanceId}"]`);
        if (!videoElement) return false;
        const videoRect = videoElement.getBoundingClientRect();
        return (
          videoRect.top < containerRect.bottom &&
          videoRect.bottom > containerRect.top
        );
      })
      .map((short) => short);

    const newVisibleVideo = visibleVideos[0];
    const newVisibleVideoId = newVisibleVideo?._instanceId || newVisibleVideo?.video_id || null;
    
    // Only update if the visible video has actually changed
    if (newVisibleVideoId !== visibleVideoId) {
      setVisibleVideo(newVisibleVideo);
      setVisibleVideoId(newVisibleVideoId);
      
      const visibleIndex = allShorts.findIndex(short => (short._instanceId || short.video_id) === newVisibleVideoId);
      if (visibleIndex !== -1) {
        const batchStartIndex = allShorts.length - currentBatch.length;
        if (visibleIndex >= batchStartIndex) {
          setCurrentBatchIndex(visibleIndex - batchStartIndex);
        }
      }
    }
  }, [allShorts, visibleVideoId, currentBatch.length]);

  useEffect(() => {

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });

    if (vanityUrl && isInitialLoad) {
      fetchSpecificShort(vanityUrl);
    } else {
      fetchInitialShorts();
    }
  }, [])

  useEffect(() => {
    if (shortsContainerRef.current) {
      shortsContainerRef.current.focus();
    }
  }, [isLoading]);

  useEffect(() => {
    if (visibleVideoId) {
      navigate(`/shorts/${visibleVideo?.vanity_url}`);
    }
  }, [visibleVideoId, navigate, visibleVideo]);

  useEffect(() => {
    if (visibleVideoId && visibleVideoId !== previousVisibleVideoId) {
      setPreviousVisibleVideoId(visibleVideoId);
      
      if (previousVisibleVideoId !== null) {
        checkAndFetchMore();
      }
    }
  }, [visibleVideoId, previousVisibleVideoId, checkAndFetchMore]);

  useEffect(() => {
    if (allShorts.length > 0 && !visibleVideoId && !isLoading) {
      setTimeout(() => {
        determineVisibleVideo();
      }, 100);
    }
  }, [allShorts, visibleVideoId, isLoading, determineVisibleVideo]);

  useEffect(() => {
    if (allShorts.length >= 20) {
      setAllShorts(prev => prev.slice(10));
    }
  }, [allShorts]);

  useEffect(() => {
    setPreviousPath(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const handlePopState = () => {
      if (previousPath && previousPath.startsWith('/shorts/')) {
        navigate('/');
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [previousPath, navigate]);

  const handleScroll = useCallback(() => {
    if (!shortsContainerRef.current) return;
    
    const scrollTop = shortsContainerRef.current.scrollTop;
    const scrollHeight = shortsContainerRef.current.scrollHeight;
    const clientHeight = shortsContainerRef.current.clientHeight;
    
    if (scrollTop + clientHeight > scrollHeight - 800) {
      checkAndFetchMore();
    }
    
    determineVisibleVideo();
    
    lastScrollTop.current = scrollTop;
  }, [checkAndFetchMore, determineVisibleVideo]);
  
  
  const fetchSpecificShort = async (vanityUrl) => {
    try {
      const response = await getSingleShorts(appInfo, vanityUrl);
      if (response?.data?.data && response.data.data.length > 0) {
        const specificShort = response.data.data[0];
        
        const responseAll = await getShorts(appInfo);
        if (responseAll?.data?.data) {
          setIsLoading(false);
          setIsInitialLoad(false);

          const allShortsData = responseAll.data.data.map((item, idx) => ({ ...item, _instanceId: `${item.video_id}-${Date.now()}-${idx}` }));
          const specificWithId = { ...specificShort, _instanceId: `${specificShort.video_id}-${Date.now()}-0` };
          const filteredData = allShortsData.filter(video => video.video_id !== specificShort.video_id);

          const combinedShorts = [specificWithId, ...filteredData];
          setAllShorts(combinedShorts);
          setCurrentBatch(combinedShorts);
          setCurrentBatchIndex(0);

          setVisibleVideo(specificWithId);
          setVisibleVideoId(specificWithId._instanceId);
        }
      }
    } catch (error) {
      console.error('Error fetching specific short:', error);
      fetchInitialShorts();
    }
  }

  const fetchInitialShorts = async () => {
    if (isFetching) return;

    setIsFetching(true);
    try {
      const response = await getShorts(appInfo);
      if (response?.data?.data && response.data.data.length > 0) {
        setIsLoading(false);
        setIsInitialLoad(false);

        const shortsData = response.data.data.map((item, idx) => ({ ...item, _instanceId: `${item.video_id}-${Date.now()}-${Math.random().toString(36).slice(2,8)}` }));
        setAllShorts(shortsData);
        setCurrentBatch(shortsData);
        setCurrentBatchIndex(0);

        if (shortsData.length === 1) {
          fetchMoreShorts();
        }
      }
    } catch (error) {
      console.error('Error fetching initial shorts:', error);
    }
    setIsFetching(false);
  };

  useEffect(() => {
    const debouncedScroll = debounce(handleScroll, 300);

    if (shortsContainerRef.current) {
      shortsContainerRef.current.addEventListener('scroll', debouncedScroll);
    }

    return () => {
      if (shortsContainerRef.current) {
        shortsContainerRef.current.removeEventListener('scroll', debouncedScroll);
      }
    };
  }, [handleScroll]);

  return (
    <div className={`shorts ${isRTL ? "rtl" : ""}`}>
      {isLoading ? (
        <div className="loader-div"><span className="loader"></span></div>
      ) : (
        <div className="shorts__videos" ref={shortsContainerRef} tabIndex={-1}>
          {allShorts
            .map((short, index) => (
              <Videos
                key={short?._instanceId}
                id={short?._instanceId}
                originalId={short?.video_id}
                src={short.url}
                like={short.like_count}
                share={0}
                shareUrl={short.share_url}
                title={short.title}
                desc={short.description}
                comment={short.comments_count}
                projectInfo={projectInfo}
                video_time={short.video_time}
                isVisible={isInitialLoad && index === 0 ? true : (short?._instanceId || short?.video_id) === visibleVideoId}
                isInitial={isInitialLoad}
                appInfo={appInfo}
                vanityUrl={short.vanity_url}
                liked_flag={short.liked ? 'blue' : null}
                poster={short?.thumbnail}
                handleScroll={handleScroll}
                ensureNextFetch={fetchMoreShorts}
                shortsContainerRef={shortsContainerRef}
                debounce={debounce}
              />
            ))}
          {isFetching && !isLoading && (
            <div className="shorts__loading">
              <span className="loader"></span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Shorts;
